import Layout from "@/components/layout/Layout";
import React, {
	useEffect,
	useState
} from "react";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import Sidepanel from "@/components/layout/Sidepanel";
import Label from "@/components/elements/Label";
import { InertiaLink } from "@inertiajs/inertia-react";
import Dropdown from "@/components/elements/Dropdown";
import TextInput from "@/components/elements/TextInput";
import DashboardMenu from "@/pages/Dashboard/DashboardMenu";
import api from "@/services/api";

/**
 * EditUser Dashboard Component
 *
 * Provides an interface for editing existing user records in the system.
 * Allows modification of user properties including name, email, password,
 * and customer association with automatic password generation capability.
 *
 * @param {Object} props - Component props
 * @param {Array} props.companies - Available companies for user assignment
 * @param {Object} props.companies[] - Company object
 * @param {string} props.companies[].id - Company ID
 * @param {string} props.companies[].name - Company name
 * @param {string} props.companies[].accountno - Company account number
 * @param {Object} props.userobject - The user object to edit
 * @param {string} props.userobject.id - User ID
 * @param {string} props.userobject.name - User name
 * @param {string} props.userobject.email - User email
 * @param {string} props.userobject.customerid - Associated customer ID
 * @param {string} props.userobject.customercode - Customer account code
 * @param {string} props.updateUserUrl - API endpoint for updating the user
 *
 * @returns {JSX.Element} The rendered edit user form page
 */
export default function NewUser({ companies, userobject, updateUserUrl }) {
    // Initialize state with existing user data
    const [user, setUser] = useState(userobject);
    const [error, setError] = useState([]);

    /**
     * Handle form submission to save user changes
     * Sends PUT request to update the user and redirects on success
     *
     * @param {Event} e - Form submit event
     */
    function saveUser(e) {
        e.preventDefault();

        api
            .put(updateUserUrl, user)
            .then((response) => {
                window.location.href = "/dashboard/users";
            })
            .catch((error) => setError(error.response.data.errors));
    }

    /**
     * Update user state with new values from TextInput components
     * Used by TextInput components for form field updates
     *
     * @param {Object} newUser - Updated user object
     */
    function updateUser(newUser) {
        setUser(newUser);
    }

    /**
     * Generate a random password for the user
     * Creates a 10-character password using letters and numbers
     */
    function buildPassword() {
        let a = "",
            b =
                "abcdefghijklmnopqrstuvwxyz1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZ",
            c = 10;
        for (let ma = 0; ma < c; ma++) {
            a += b[Math.floor(Math.random() * b.length)];
        }
        let us = { ...user };
        us["password"] = a;
        setUser(us);
    }

    /**
     * Handle customer dropdown selection changes
     * Updates user with selected customer information and auto-populates fields
     *
     * @param {*} value - Selected dropdown value (customer ID)
     * @param {string} property - Property being updated (customerid)
     */
    function updateDropdown(value, property) {
        let us = { ...value };
        us[property] = parseInt(value);

        // Find selected company and auto-populate user details
        let company = companies.find((x) => x.id === parseInt(value));
        if (company) {
            us["name"] = company.name;
            us["customercode"] = company.accountno;
        }
        setUser(us);
    }

    return (
        <Layout>
            <Sidepanel>
                <DashboardMenu active={"Gebruikers"} />
            </Sidepanel>
            <Content>
                <ContentOptions small>
                    <Heading heading={"Nieuwe gebruiker"} />
                </ContentOptions>
                <div
                    className={`contentoptions ingaveoptions col-md-9 d-flex flex-column`}
                >
                    <Heading heading="Pas gebruiker aan">
                        <div>
                            <InertiaLink
                                href="/dashboard/users"
                                className="btn-orange"
                                name={"Terug"}
                            >
                                terug
                            </InertiaLink>
                            <a
                                name={"Opslaan"}
                                href="#"
                                onClick={saveUser}
                                className="btn-orange"
                            >
                                opslaan
                            </a>
                        </div>
                    </Heading>

                    <div className="details">
                        <div className="question mb-3">
                            <Label label="Klant" small />
                            <Dropdown
                                properties={user}
                                property={"customerid"}
                                updateProperties={updateDropdown}
                                notSelectedDisabled={false}
                                notSelectedText="-- Selecteer een klant --"
                                options={companies.map((company) => {
                                    return {
                                        name:
                                            company.accountno +
                                            " - " +
                                            company.name,
                                        value: company.id,
                                    };
                                })}
                            />
                        </div>
                        <div className="question mb-3">
                            <Label label="Naam" small />
                            <TextInput
                                properties={user}
                                property="name"
                                updateProperties={updateUser}
                            />
                            {error && error.name ? (
                                <label className="labelsmall text-danger">
                                    {error.name[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <Label label="E-mail" small />
                            <TextInput
                                properties={user}
                                property="email"
                                updateProperties={updateUser}
                            />
                            {error && error.email ? (
                                <label className="labelsmall text-danger">
                                    {error.email[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <Label label="Wachtwoord" small />
                            <TextInput
                                properties={user}
                                property="password"
                                updateProperties={updateUser}
                            />
                            {error && error.password ? (
                                <label className="labelsmall text-danger">
                                    {error.password[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <a href="#" className="btn-orange" onClick={buildPassword}>Genereer nieuw wachtwoord</a>
                        </div>
                    </div>
                </div>
            </Content>
        </Layout>
    );
}
